import React, { useState, useEffect } from 'react';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import LoginIcon from '@mui/icons-material/Login';

export default function Login({ onLogin }) {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, password })
      });
      const data = await response.json();
      if (response.ok) {
        onLogin(data);
      } else {
        setError(data.message || 'Login failed.');
      }
    } catch {
      setError('Cannot connect to server.');
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') handleLogin(); };

  return (
    <div style={styles.page}>
      <div style={styles.blob1} />
      <div style={styles.blob2} />

      {/* Single animated container */}
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0px) scale(1)' : 'translateY(60px) scale(0.98)',
        transition: 'opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'opacity, transform',
      }}>
        <main style={styles.main}>
          {/* Logo & Branding */}
          <div style={styles.header}>
            <div style={styles.logoBox}>
              <MedicalServicesIcon style={{ fontSize: '48px', color: '#0052CC' }} />
            </div>
            <h1 style={styles.title}>MyHealth Journal</h1>
            <p style={styles.subtitle}>
              Secure access to your clinical records and comprehensive health management.
            </p>
          </div>

          {/* Login Card */}
          <div style={styles.card}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Phone Number</label>
              <div style={styles.inputWrapper}>
                <PhoneIcon style={styles.fieldIcon} />
                <input
                  style={styles.input}
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <div style={styles.labelRow}>
                <label style={styles.label}>Password</label>
                <span style={styles.forgotLink}>Forgot password?</span>
              </div>
              <div style={styles.inputWrapper}>
                <LockIcon style={styles.fieldIcon} />
                <input
                  style={{ ...styles.input, paddingRight: '48px' }}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button style={styles.eyeBtn} onClick={() => setShowPassword(!showPassword)} type="button">
                  {showPassword
                    ? <VisibilityOffIcon style={{ fontSize: '20px', color: '#737685' }} />
                    : <VisibilityIcon style={{ fontSize: '20px', color: '#737685' }} />
                  }
                </button>
              </div>
            </div>

            <div style={styles.rememberRow}>
              <input
                type="checkbox"
                id="remember"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                style={styles.checkbox}
              />
              <label htmlFor="remember" style={styles.rememberLabel}>
                Keep me logged in for 30 days
              </label>
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <button
              style={{ ...styles.loginBtn, opacity: loading ? 0.8 : 1 }}
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? 'Authenticating...' : 'Login'}
              {!loading && <LoginIcon style={{ fontSize: '20px', marginLeft: '8px' }} />}
            </button>

            <div style={styles.divider} />

            <p style={styles.registerText}>
              Don't have an account yet?{' '}
              <strong
                style={styles.registerLink}
                onClick={() => onLogin({ goToRegister: true })}
              >
                Register
              </strong>
            </p>
          </div>

          {/* Footer */}
          <footer style={styles.footer}>
            <div style={styles.trustRow}>
              <div style={styles.trustItem}>
                <VerifiedUserIcon style={{ fontSize: '14px', color: '#737685' }} />
                <span style={styles.trustText}>HIPAA Compliant</span>
              </div>
              <span style={styles.dot}>•</span>
              <div style={styles.trustItem}>
                <EnhancedEncryptionIcon style={{ fontSize: '14px', color: '#737685' }} />
                <span style={styles.trustText}>AES-256 Encryption</span>
              </div>
            </div>
            <div style={styles.footerLinks}>
              <span style={styles.footerLink}>Terms of Service</span>
              <span style={styles.footerLink}>Privacy Policy</span>
              <span style={styles.footerLink}>Support</span>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #eef1f6 0%, #e8ecf3 40%, #dce3ee 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: '24px',
    position: 'relative',
    overflow: 'hidden',
  },
  blob1: { position: 'fixed', top: '-10%', right: '-5%', width: '500px', height: '500px', borderRadius: '50%', background: 'rgba(0, 82, 204, 0.05)', filter: 'blur(100px)', pointerEvents: 'none' },
  blob2: { position: 'fixed', bottom: '-10%', left: '-5%', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(0, 104, 123, 0.04)', filter: 'blur(100px)', pointerEvents: 'none' },
  main: { position: 'relative', zIndex: 10, width: '100%', maxWidth: '480px' },
  header: { textAlign: 'center', marginBottom: '32px', paddingTop: '16px' },
  logoBox: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', background: 'rgba(0, 82, 204, 0.08)', borderRadius: '20px', marginBottom: '20px', border: '1px solid rgba(0, 82, 204, 0.1)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  title: { fontSize: '36px', fontWeight: '800', color: '#0a0f1e', margin: '0 0 12px', letterSpacing: '-0.03em', fontFamily: "'Inter', sans-serif", lineHeight: '1.1' },
  subtitle: { fontSize: '15px', color: '#64748b', margin: '0 auto', lineHeight: '24px', maxWidth: '340px', fontWeight: '400', letterSpacing: '0.01em' },
  card: { background: '#ffffff', border: '1px solid #dde1ea', borderRadius: '16px', padding: '40px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' },
  fieldGroup: { marginBottom: '22px' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  label: { display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '8px', letterSpacing: '0.06em', textTransform: 'uppercase' },
  forgotLink: { fontSize: '13px', color: '#0052CC', cursor: 'pointer', fontWeight: '500', letterSpacing: '0', textTransform: 'none' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  fieldIcon: { position: 'absolute', left: '14px', fontSize: '18px', color: '#94a3b8', zIndex: 1 },
  input: { width: '100%', paddingLeft: '44px', paddingRight: '16px', paddingTop: '13px', paddingBottom: '13px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', color: '#0f172a', outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", fontWeight: '400', letterSpacing: '0.01em', transition: 'border-color 0.2s, box-shadow 0.2s' },
  eyeBtn: { position: 'absolute', right: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' },
  rememberRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' },
  checkbox: { width: '16px', height: '16px', cursor: 'pointer', accentColor: '#0052CC' },
  rememberLabel: { fontSize: '13px', color: '#64748b', cursor: 'pointer', fontWeight: '400', letterSpacing: '0.01em' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', fontWeight: '500' },
  loginBtn: { width: '100%', padding: '15px', background: 'linear-gradient(135deg, #0052CC 0%, #003d9b 100%)', color: '#ffffff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Inter', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', letterSpacing: '0.03em', boxShadow: '0 4px 14px rgba(0, 82, 204, 0.35)', transition: 'all 0.2s' },
  divider: { height: '1px', background: '#f1f5f9', margin: '28px 0' },
  registerText: { textAlign: 'center', fontSize: '15px', color: '#64748b', margin: 0, fontWeight: '400', letterSpacing: '0.01em' },
  registerLink: { color: '#0052CC', cursor: 'pointer', fontWeight: '700', letterSpacing: '0' },
  footer: { marginTop: '32px', textAlign: 'center', paddingBottom: '16px' },
  trustRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '14px', opacity: 0.5 },
  trustItem: { display: 'flex', alignItems: 'center', gap: '5px' },
  trustText: { fontSize: '12px', color: '#64748b', fontWeight: '500', letterSpacing: '0.02em' },
  dot: { color: '#94a3b8', fontSize: '12px' },
  footerLinks: { display: 'flex', justifyContent: 'center', gap: '24px' },
  footerLink: { fontSize: '12px', color: '#94a3b8', cursor: 'pointer', fontWeight: '400', letterSpacing: '0.01em' },
};